import { useMutation, useQueryClient } from '@tanstack/react-query';
import { intelligenceMemory, type intelligencePersonalization } from '@cwos/api-client';
import { SettingsCard } from './common.js';

type MemoryRecord = intelligencePersonalization.SubjectMemoryRecord;

/**
 * "What Phibian has learned about you" — the presentation preferences Phibian
 * picked up from what this person said in Ask Phibian (workstream L). Each can
 * be changed (a new value supersedes the old) or turned off (revoked; the next
 * answer no longer follows it). Only the caller's own record ever reaches here.
 */
export function LearnedPreferences({
  records,
  queryKey,
}: {
  records: readonly MemoryRecord[];
  queryKey: readonly unknown[];
}) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey });
  const change = useMutation({
    mutationFn: (v: { kind: string; value: string }) =>
      intelligenceMemory.setPreference(v.kind, v.value),
    onSuccess: refresh,
  });
  const turnOff = useMutation({
    mutationFn: (memoryId: string) => intelligenceMemory.revoke(memoryId),
    onSuccess: refresh,
  });
  const learned = records.filter(
    (r) => r.source === intelligenceMemory.LEARNED_PREFERENCE_SOURCE && r.intelligenceUsesThis,
  );
  const busy = change.isPending || turnOff.isPending;

  return (
    <SettingsCard>
      <h2 className="rn-card-title">What Phibian has learned about you</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        When you tell Phibian how you like answers — “shorter please”, “use a table”, “always break
        it down by program” — it remembers that for you alone and follows it from the next answer
        on. It only ever changes how an answer is presented, never what is true or what you may see.
      </p>
      {learned.length === 0 ? (
        <p style={{ marginTop: 'var(--space-8)' }} data-testid="learned-none">
          Nothing yet.
        </p>
      ) : (
        <ul style={{ marginTop: 'var(--space-8)' }} data-testid="learned-list">
          {learned.map((r) => {
            const kind = r.payload.kind;
            const choice = intelligenceMemory.PREFERENCE_CHOICES[kind];
            const value = r.payload.labels?.[0] ?? '';
            return (
              <li key={r.memoryId} style={{ marginBottom: 'var(--space-8)' }}>
                <label>
                  <strong>{choice?.label ?? kind}</strong>{' '}
                  <select
                    aria-label={`Change ${choice?.label ?? kind}`}
                    value={value}
                    disabled={busy || !choice}
                    onChange={(e) => change.mutate({ kind, value: e.target.value })}
                  >
                    {Object.entries(choice?.values ?? { [value]: value }).map(([v, text]) => (
                      <option key={v} value={v}>
                        {text}
                      </option>
                    ))}
                  </select>
                </label>{' '}
                <button type="button" disabled={busy} onClick={() => turnOff.mutate(r.memoryId)}>
                  Turn off
                </button>
                <div className="rn-muted">
                  Learned {new Date(r.timestamp).toLocaleDateString()} from your own words in Ask
                  Phibian · confidence {Math.round(r.confidence * 100)}%
                  {r.payload.note ? ` · ${r.payload.note}` : ''}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {change.isError || turnOff.isError ? (
        <p role="alert">That did not save. Nothing changed — try again.</p>
      ) : null}
    </SettingsCard>
  );
}
